import * as React from "react";
import { Alert, Box, Button } from "@mui/material";
import { Layers, Plus } from "lucide-react";
import { ACTIVE_BATCH_STATUSES, listBatches, type Batch } from "../../api/batches";
import { Col } from "../Common/Row";
import { PageHeader } from "../Common/PageHeader";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { BatchRow } from "./BatchRow";
import { NewBatchDialog } from "./NewBatchDialog";
import { BatchDetail } from "./BatchDetail";
import { useLatestPoll } from "./useLatestPoll";

export const BATCH_POLL_MS = 3000;

export function BatchesPage() {
  const [batches, setBatches] = React.useState<Batch[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);
  const [openId, setOpenId] = React.useState<string | null>(null);

  // Poll only while something is active so an idle tab makes no requests.
  const anyActive = !!batches?.some((b) => ACTIVE_BATCH_STATUSES.includes(b.status));
  const refresh = useLatestPoll(
    listBatches,
    (b) => { setError(null); setBatches(b); },
    (e) => setError(e instanceof Error ? e.message : String(e)),
    anyActive && !openId,
    BATCH_POLL_MS,
  );

  React.useEffect(() => { void refresh(); }, [refresh]);

  if (openId) {
    return (
      <BatchDetail
        batchId={openId}
        onBack={() => { setOpenId(null); void refresh(); }}
      />
    );
  }

  // The header's and the empty state's copies are tagged apart.
  const newButton = (inHeader: boolean) => (
    <Button
      variant="contained"
      startIcon={<Plus size={16} />}
      onClick={() => setFormOpen(true)}
      data-testid="new-batch-button"
      data-ph={inHeader ? "header-create" : "empty-create"}
    >
      New batch
    </Button>
  );

  return (
    <Box>
      <PageHeader
        title="Batches"
        subtitle="Run many files through the same workflow and download the results as one zip."
        actions={newButton(true)}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!error && batches === null && <CardSkeletonList />}

      {batches !== null && batches.length === 0 && (
        <EmptyState
          icon={Layers}
          title="No batches yet"
          description="Upload a set of invoices and contracts (or a zip) and Artie audits each one for you."
          action={newButton(false)}
        />
      )}

      {batches !== null && batches.length > 0 && (
        <Col gap={1} data-ph-scope="list">
          {batches.map((b) => (
            <BatchRow key={b.id} batch={b} onOpen={setOpenId} />
          ))}
        </Col>
      )}

      <NewBatchDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(id) => { setFormOpen(false); void refresh(); setOpenId(id); }}
      />
    </Box>
  );
}
